/**
 * Client-side market arithmetic — an exact mirror of the server's.
 *
 * WHY A MIRROR RATHER THAN A SERVER ROUND-TRIP: the assumptions editor
 * recalculates while a founder types, and a network call per keystroke is both
 * slow and a way to spend money on arithmetic. So the formulas run locally.
 *
 * WHY THAT IS SAFE: this preview is NEVER authoritative. The server recomputes
 * everything on save, and the published report carries the server's numbers. The
 * only thing that could go wrong is the two disagreeing — a founder watching a
 * figure change after pressing Save, with no explanation, having already
 * screenshotted the wrong one for their deck.
 *
 * `market-parity-fixtures.ts` is the defence: one table of inputs and expected
 * outputs, asserted by the Deno suite against the server implementation and by
 * the Jest suite against this one. Neither can drift without going red.
 *
 * Keep this file in lockstep with
 * `supabase/functions/_shared/strategic-analysis/methodology/market-validation.ts`.
 * A change to either is a methodology change, which means a version increment.
 */

export interface Range {
  low: number;
  base: number;
  high: number;
}

/** Methods disagreeing by more than this multiple produce no reference range. */
export const MARKET_METHOD_CONFLICT_RATIO = 2;
/** How far a founder range may sit outside the independent one before it is called. */
export const MARKET_FOUNDER_TOLERANCE = 0.25;

export type MarketConclusion =
  | 'supported'
  | 'plausible_with_caveats'
  | 'potentially_overstated'
  | 'potentially_understated'
  | 'not_comparable'
  | 'insufficient_evidence';

function assertUsableRange(label: string, r: Range): void {
  for (const [k, v] of Object.entries(r)) {
    if (!Number.isFinite(v)) throw new Error(`market: ${label}.${k} is not a finite number`);
    if (v < 0) throw new Error(`market: ${label}.${k} is negative`);
  }
  if (!(r.low <= r.base && r.base <= r.high)) {
    throw new Error(`market: ${label} bounds are not ordered low <= base <= high`);
  }
}

/**
 * Non-throwing check for the editor.
 *
 * The calculation itself throws, exactly as the server's does, because a bad
 * range must never quietly produce a number. But an editor cannot throw at every
 * intermediate keystroke, so it asks first and shows the reason.
 */
export function describeRangeProblem(label: string, r: Range): string | null {
  try {
    assertUsableRange(label, r);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'market: invalid range';
  }
}

export interface BottomUpInput {
  /** Absent means TAM is NOT computable — see below. */
  totalCustomerUnits?: Range;
  serviceableCustomerUnits: Range;
  annualValuePerCustomer: Range;
  reachableShare: Range;
}

export interface BottomUpResult {
  /**
   * NULL when total potential units were never provided. Reusing the
   * serviceable count would silently assert the startup can serve the entire
   * market — a substantive claim manufactured by a missing input.
   */
  tam: Range | null;
  sam: Range;
  som: Range;
  transformations: string[];
}

const mul = (a: Range, b: Range): Range => ({
  low: a.low * b.low,
  base: a.base * b.base,
  high: a.high * b.high,
});

/**
 * The three fixed formulas:
 *
 *   TAM = all potential customer units x annual value per customer
 *   SAM = serviceable customer units   x annual value per customer
 *   SOM = SAM                          x realistically reachable share
 *
 * Ranges combine low-with-low and high-with-high — the widest honest envelope,
 * and deliberately not a probabilistic combination, which would imply a
 * distribution nobody estimated.
 */
export function calculateBottomUp(input: BottomUpInput): BottomUpResult {
  assertUsableRange('serviceableCustomerUnits', input.serviceableCustomerUnits);
  assertUsableRange('annualValuePerCustomer', input.annualValuePerCustomer);
  assertUsableRange('reachableShare', input.reachableShare);
  if (input.totalCustomerUnits) assertUsableRange('totalCustomerUnits', input.totalCustomerUnits);

  if (input.reachableShare.high > 1) {
    throw new Error('market: reachableShare cannot exceed 1 (100% of the serviceable market)');
  }

  const sam = mul(input.serviceableCustomerUnits, input.annualValuePerCustomer);
  const som = mul(sam, input.reachableShare);
  const tam = input.totalCustomerUnits
    ? mul(input.totalCustomerUnits, input.annualValuePerCustomer)
    : null;

  const transformations = [
    'SAM = serviceable customer units x annual value per customer',
    'SOM = SAM x realistically reachable share',
  ];
  if (tam) transformations.unshift('TAM = all potential customer units x annual value per customer');
  else transformations.push('TAM not computed: total potential customer units were not provided');

  return { tam, sam, som, transformations };
}

export interface DefinitionAlignment {
  currencyMatches: boolean;
  baseYearMatches: boolean;
  definitionMatches: boolean;
  customerUnitMatches: boolean;
  geographyMatches: boolean;
}

export interface MethodRange {
  supported: boolean;
  low?: number;
  base?: number;
  high?: number;
}

export interface CompareInput {
  alignment: DefinitionAlignment;
  topDown: MethodRange;
  bottomUp: MethodRange;
  founder: Range | null;
}

export interface CompareResult {
  conclusion: MarketConclusion;
  corroboratedRange: Range | null;
  methodConflict: boolean;
  /** When only one method is usable, which one — so the UI can still show it. */
  usableMethod: 'top_down' | 'bottom_up' | null;
  explanation: string;
}

function complete(m: MethodRange): m is MethodRange & Range {
  return (
    m.supported && Number.isFinite(m.low) && Number.isFinite(m.base) && Number.isFinite(m.high)
  );
}

export function compareMarketRanges(input: CompareInput): CompareResult {
  const { alignment, topDown, bottomUp, founder } = input;

  // 1. Compatibility BEFORE arithmetic. Comparing a Swiss-franc 2026 serviceable
  //    market to a euro 2024 total market produces a number that means nothing.
  const misaligned = Object.entries(alignment)
    .filter(([, ok]) => !ok)
    .map(([k]) => k.replace('Matches', ''));
  if (misaligned.length > 0) {
    return {
      conclusion: 'not_comparable',
      corroboratedRange: null,
      methodConflict: false,
      usableMethod: null,
      explanation: `The figures are not directly comparable: ${misaligned.join(
        ', ',
      )} differ between the founder assumptions and the researched evidence. A number computed across them would not mean anything.`,
    };
  }

  if (founder && !(founder.low <= founder.base && founder.base <= founder.high)) {
    return {
      conclusion: 'not_comparable',
      corroboratedRange: null,
      methodConflict: false,
      usableMethod: null,
      explanation:
        'The founder range bounds are not ordered low to high, so no comparison was attempted. Reordering them silently would change what was submitted.',
    };
  }

  // 2. Both methods must be supported. One method alone is a single unchecked
  //    estimate, so it is shown separately rather than turned into a verdict.
  const topOk = complete(topDown);
  const bottomOk = complete(bottomUp);
  if (!topOk || !bottomOk) {
    return {
      conclusion: 'insufficient_evidence',
      corroboratedRange: null,
      methodConflict: false,
      usableMethod: topOk ? 'top_down' : bottomOk ? 'bottom_up' : null,
      explanation:
        'A numerical comparison requires both a supported top-down and a supported bottom-up range. Only one method had sufficient evidence, so it is shown on its own rather than presented as a corroborated figure.',
    };
  }

  const td = topDown as Range;
  const bu = bottomUp as Range;

  // 3. Method agreement.
  const overlaps = td.low <= bu.high && bu.low <= td.high;
  let corroboratedRange: Range | null;
  let methodConflict: boolean;

  if (overlaps) {
    const low = Math.max(td.low, bu.low);
    const high = Math.min(td.high, bu.high);
    corroboratedRange = { low, base: (low + high) / 2, high };
    methodConflict = false;
  } else {
    const ratio = Math.max(td.base, bu.base) / Math.min(td.base, bu.base);
    if (ratio > MARKET_METHOD_CONFLICT_RATIO) {
      return {
        conclusion: 'insufficient_evidence',
        corroboratedRange: null,
        methodConflict: true,
        usableMethod: null,
        explanation: `The two methods differ by more than ${MARKET_METHOD_CONFLICT_RATIO}x (${ratio.toFixed(
          1,
        )}x). No reference range is published: averaging methods that disagree this much would manufacture a figure neither of them supports.`,
      };
    }
    const low = Math.min(td.low, bu.low);
    const high = Math.max(td.high, bu.high);
    corroboratedRange = { low, base: (low + high) / 2, high };
    methodConflict = true;
  }

  // 4. Founder comparison.
  if (!founder) {
    return {
      conclusion: 'insufficient_evidence',
      corroboratedRange,
      methodConflict,
      usableMethod: null,
      explanation:
        'An independent range was produced, but no founder TAM/SAM/SOM was supplied, so there is nothing to compare it against.',
    };
  }

  const ind = corroboratedRange;
  const founderOverlaps = founder.low <= ind.high && ind.low <= founder.high;

  if (founderOverlaps) {
    if (methodConflict) {
      return {
        conclusion: 'plausible_with_caveats',
        corroboratedRange,
        methodConflict,
        usableMethod: null,
        explanation:
          'The founder range overlaps the combined envelope of two methods that did not agree. Overlapping a conflicted envelope is weaker evidence than overlapping a corroborated range, so this is reported with a caveat rather than as supported.',
      };
    }
    return {
      conclusion: 'supported',
      corroboratedRange,
      methodConflict,
      usableMethod: null,
      explanation:
        'The founder range overlaps the range where the top-down and bottom-up methods corroborate each other.',
    };
  }

  const overstatedThreshold = ind.high * (1 + MARKET_FOUNDER_TOLERANCE);
  const understatedThreshold = ind.low * (1 - MARKET_FOUNDER_TOLERANCE);

  // Strictly greater / less: exactly at the tolerance is still within it.
  if (founder.low > overstatedThreshold) {
    return {
      conclusion: 'potentially_overstated',
      corroboratedRange,
      methodConflict,
      usableMethod: null,
      explanation: `The founder low bound sits more than ${
        MARKET_FOUNDER_TOLERANCE * 100
      }% above the independent high bound.`,
    };
  }
  if (founder.high < understatedThreshold) {
    return {
      conclusion: 'potentially_understated',
      corroboratedRange,
      methodConflict,
      usableMethod: null,
      explanation: `The founder high bound sits more than ${
        MARKET_FOUNDER_TOLERANCE * 100
      }% below the independent low bound.`,
    };
  }

  return {
    conclusion: 'plausible_with_caveats',
    corroboratedRange,
    methodConflict,
    usableMethod: null,
    explanation: `The founder range does not overlap the independent range, but its nearest bound is within ${
      MARKET_FOUNDER_TOLERANCE * 100
    }% of it.`,
  };
}

/**
 * Parse a founder-typed number.
 *
 * Accepts the separators a European founder actually types — `1'200`, `1 200`,
 * `1,200` — and returns null rather than NaN for anything else, so an
 * unparseable entry stays missing instead of becoming a number.
 */
export function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s'’]/g, '').replace(/,(?=\d{3}\b)/g, '');
  if (cleaned.length === 0) return null;
  const value = Number(cleaned.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

/** Currency-aware display. Never rounds before a comparison — only for display. */
export function formatAmount(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${Math.round(value).toLocaleString('en-CH')}`;
  }
}

export function formatRange(range: Range, currency: string): string {
  return `${formatAmount(range.low, currency)} – ${formatAmount(range.high, currency)}`;
}

/**
 * Compact display: `CHF 480K`, `CHF 21.6M`, `CHF 1.2B`.
 *
 * Market figures are read as magnitudes, not as amounts anyone will reconcile.
 * `CHF 21'600'000` costs a reader three glances to place; `CHF 21.6M` costs
 * one, and on a phone the long form is what pushes a comparison row onto two
 * lines. The exact form stays available in `formatAmount` for anywhere a
 * figure has to be transcribed.
 *
 * ROUNDING NEVER ADDS PRECISION. One decimal below 100 units, none above, so
 * 21.6M stays 21.6M and 480K stays 480K — a figure is never dressed up as
 * finer than it arrived.
 */
export function formatCompactAmount(value: number, currency: string): string {
  if (!Number.isFinite(value)) return `${currency} —`;
  const abs = Math.abs(value);
  let scaled = value;
  let suffix = '';
  if (abs >= 1_000_000_000) {
    scaled = value / 1_000_000_000;
    suffix = 'B';
  } else if (abs >= 1_000_000) {
    scaled = value / 1_000_000;
    suffix = 'M';
  } else if (abs >= 1_000) {
    scaled = value / 1_000;
    suffix = 'K';
  }
  if (!suffix) return `${currency} ${Math.round(value).toLocaleString('en-CH')}`;
  const digits = Math.abs(scaled) < 100 ? 1 : 0;
  const text = scaled.toFixed(digits).replace(/\.0$/, '');
  return `${currency} ${text}${suffix}`;
}

export function formatCompactRange(range: Range, currency: string): string {
  return `${formatCompactAmount(range.low, currency)} – ${formatCompactAmount(range.high, currency)}`;
}
